import * as React from 'react';
import * as ReactDOM from 'react-dom';
import ReactDatePicker, {
  ReactDatePickerProps,
} from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    (navigator && navigator.maxTouchPoints > 0) ||
    window.matchMedia('(pointer: coarse)').matches
  );
};

// Render the calendar popper at <body> level. Inside a draggable modal the
// popper is otherwise a descendant of an element with a CSS `transform`
// (react-draggable) plus `overflow: hidden` — that confuses Popper.js v1 on
// spec-compliant browsers (Chrome/Edge) at non-100% display scaling, so the
// calendar lands detached from the input and gets clipped (one machine sees it,
// another doesn't). Portalling it out of the transformed ancestor fixes the
// positioning everywhere.
//
// Side effect to guard against: the calendar is now outside any modal's MUI
// <ClickAwayListener>, so a native mousedown on a day would otherwise be seen as
// an "away" click and close the modal. We swallow mousedown/touchend at the
// portal root so it never reaches the document-level listeners; the follow-up
// `click` (used by react-datepicker for selection) is untouched.
export const PopperPortal = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const stop = (e: Event) => e.stopPropagation();
    node.addEventListener('mousedown', stop);
    node.addEventListener('touchend', stop);
    return () => {
      node.removeEventListener('mousedown', stop);
      node.removeEventListener('touchend', stop);
    };
  }, []);

  if (typeof document === 'undefined') return <>{children}</>;
  return ReactDOM.createPortal(
    <div ref={ref}>{children}</div>,
    document.body,
  );
};

type Props = ReactDatePickerProps & {
  // Opt in to render the calendar in a <body> portal (see PopperPortal). Use
  // for any date picker living inside a draggable / overflow-hidden modal.
  portalPopper?: boolean;
};

const DatePicker = ({ portalPopper, ...props }: Props) => {
  const [touch] = React.useState<boolean>(isTouchDevice);

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = e => {
    if (touch) {
      // Suppress the on-screen keyboard on touch devices so users pick a date
      // from the calendar instead of typing it.
      //
      // Do NOT add `readonly` here. `isTouchDevice()` is true for ANY
      // touch-capable machine — including Windows touchscreen laptops — and
      // TextInput styles `input[readonly]` as disabled (grey bg, not-allowed
      // cursor) while `readonly` also blocks typing/clearing. That made date
      // fields (e.g. Inspection Date after switching to "Not Scheduled") look
      // greyed-out and un-clearable for touchscreen users while working fine on
      // mouse-only desktops. `inputmode="none"` alone hides the virtual
      // keyboard without disabling the field, keeping it editable everywhere.
      e.target.setAttribute('inputmode', 'none');
    }
    props.onFocus?.(e);
  };

  // Force the popper path (not `withPortal`, whose `position: fixed` overlay is
  // itself broken by a transformed modal ancestor) and portal it to <body>.
  const portalProps = portalPopper
    ? {
        withPortal: false,
        popperContainer: PopperPortal,
        popperProps: {
          positionFixed: true,
          ...((props.popperProps as object) || {}),
        },
        popperClassName: ['ep-datepicker-popper', props.popperClassName]
          .filter(Boolean)
          .join(' '),
      }
    : { withPortal: props.withPortal ?? touch };

  return (
    <ReactDatePicker
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...portalProps}
      onFocus={handleFocus}
    />
  );
};

export default DatePicker;
